import { ReactComponent as SaveIcon } from 'assets/icons/np_save_2209758_4C5AF6.svg';
import axios from 'axios';
import RichTextEditor from 'components/Editors/RichTextEditor/RichTextEditor';
import NewlineText from 'components/NewLineText/NewLineText';
import UserAvatar from 'components/UserAvatar/UserAvatar';
import { Bug, BugPriorities } from 'models/Bug';
import {
  CommentIntegrationMessageType,
  CommentNotificationType,
  CommentTypes,
} from 'models/Comment';
import { useState } from 'react';
import Lightbox from 'react-image-lightbox';
import TimeAgo from 'react-timeago';
import Swal from 'sweetalert2';
import * as timeago from 'timeago.js';
import './Comment.scss';
import { cleanupName } from 'components/FeedbackUserFilter/FeedbackUserFilter';
import PublicSkeleton from 'components/Skeletons/PublicSkeleton';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';
import englishShortStrings from 'react-timeago/lib/language-strings/en-short';
import { contentWithVariables } from 'services/ContentReplace';
import { getSessionName } from 'services/GuestNameHelper';
const formatter = buildFormatter(englishShortStrings);

export const canDeleteComment = (comment, userId, deleteDelegate) => {
  // Owner
  if (comment.user && comment.user.id === userId) {
    return deleteDelegate;
  }

  // Usergenerated content
  if (
    (userId && comment.type === CommentTypes.USER_TEXT) ||
    comment.type === CommentTypes.SHARED_COMMENT
  ) {
    return deleteDelegate;
  }

  return null;
};

export const getCommentContent = (comment) => {
  if (comment?.data?.text) {
    return comment.data.text;
  }

  return '';
};

export const getIntegrationCommentContent = (comment) => {
  const linkUrl = comment.data?.data?.url;

  switch (comment.data.actionType) {
    case CommentIntegrationMessageType.CREATED:
      return (
        <>
          Created {getFormattedLink({ label: 'ticket', link: linkUrl })} in{' '}
          <span className="capitalized">{comment.data.data.integration}</span>
        </>
      );

    case CommentIntegrationMessageType.STATUS_UPDATED:
      return (
        <>
          <span className="capitalized">{comment.data.data.integration}</span>{' '}
          {getFormattedLink({ label: 'ticket', link: linkUrl })} status changed
          to {comment.data.data.status}
        </>
      );

    case CommentIntegrationMessageType.LABELED:
      return (
        <>
          <span className="capitalized">{comment.data.data.integration}</span>{' '}
          {getFormattedLink({ label: 'ticket', link: linkUrl })} labeled{' '}
          <b>{comment.data.data.label}</b>
        </>
      );

    case CommentIntegrationMessageType.DELETED:
      return (
        <>
          Ticket in{' '}
          <span className="capitalized">{comment.data.data.integration}</span>{' '}
          deleted
        </>
      );

    default:
      return "Message can't be displayed";
  }
};

const getFormattedLink = ({ label, link }) => {
  return (
    <>
      {link ? (
        <a target="_blank" href={link} rel="noreferrer">
          ticket
        </a>
      ) : (
        label
      )}
    </>
  );
};

const getCommentUser = (comment) => {
  if (comment.user) {
    return {
      email: comment.user.email,
      name: `${comment.user.firstName} ${comment.user.lastName}`,
      sessionId: null,
      profileImageUrl: comment.user.profileImageUrl,
      isOnline: false,
    };
  }

  if (comment.session) {
    return {
      email: comment.session.email ?? '--',
      name: cleanupName(comment.session.name ?? '--', 100),
      sessionId: comment.session.id,
      profileImageUrl: comment.session.profileImageUrl
        ? comment.session.profileImageUrl
        : null,
      isOnline: comment.session?.lastActivity
        ? (Date.now() - Date.parse(comment.session?.lastActivity)) / 60000 < 2
        : false,
    };
  }

  if (
    comment.fallbackUser &&
    (comment.fallbackUser.email || comment.fallbackUser.username)
  ) {
    return {
      email: comment.fallbackUser.email,
      name: comment.fallbackUser.username,
      sessionId: null,
      isOnline: false,
    };
  }

  if (comment.type === CommentTypes.BOT || comment.bot) {
    return {
      email: 'bot',
      name: 'Bot',
      sessionId: null,
      isOnline: true,
    };
  }

  return {
    email: '--',
    name: 'Not set',
    sessionId: null,
    isOnline: false,
  };
};

const historyTextFromStatus = (emailStatus) => {
  return `<div class="emailstatus-row">${textFromStatus(
    emailStatus,
  )} <span>${timeago.format(emailStatus.eventDate ?? new Date())}</span></div>`;
};

const textFromStatus = (emailStatus) => {
  let status = 'Processing';
  if (emailStatus.event === 'processed') {
    status = 'Email sent';
  }
  if (emailStatus.event === 'deferred') {
    status = 'Email deferred';
  }
  if (emailStatus.event === 'delivered' || emailStatus.event === 'Delivery') {
    status = 'Email delivered';
  }
  if (
    emailStatus.event === 'open' ||
    emailStatus.event === 'opened' ||
    emailStatus.event === 'Open'
  ) {
    status = 'Email opened';
  }
  if (
    emailStatus.event === 'click' ||
    emailStatus.event === 'clicked' ||
    emailStatus.event === 'Click'
  ) {
    status = 'Link in email clicked';
  }
  if (emailStatus.event === 'bounce' || emailStatus.event === 'Bounce') {
    status = 'Email bounced';
  }
  if (emailStatus.event === 'dropped') {
    status = 'Email dropped';
  }
  if (emailStatus.event === 'permanent_fail') {
    status = 'Delivery failed permanently';
  }
  if (emailStatus.event === 'temporary_fail') {
    status = 'Delivery failed temporarily';
  }
  if (
    emailStatus.event === 'unsubscribed' ||
    emailStatus.event === 'SubscriptionChange'
  ) {
    status = 'User unsubscribed';
  }
  if (
    emailStatus.event === 'spamreport' ||
    emailStatus.event === 'SpamComplaint' ||
    emailStatus.event === 'spamreport'
  ) {
    status = 'Email reported as spam';
  }
  return status;
};

interface CommentProps {
  comment: any;
  onDeleteComment?: any;
  hideFooter?: boolean;
  shared?: boolean;
  possibleStatusList?: any[];
  feedbackTypes?: any[];
  hideCreator?: boolean;
  associatedBug?: Bug;
}

const Comment = ({
  comment,
  onDeleteComment,
  hideFooter = false,
  shared = true,
  possibleStatusList = [],
  feedbackTypes = [],
  hideCreator = false,
  associatedBug,
}: CommentProps) => {
  const user = getCommentUser(comment);

  const [showAttachmentImageLightBox, setShowAttachmentImageLightBox] =
    useState(false);
  const [pickedAttachmentUrl, setPickedAttachmentUrl] = useState('');

  const renderEmailStatus = () => {
    if (shared) {
      return null;
    }

    if (
      comment.type !== CommentTypes.TEXT &&
      comment.type !== CommentTypes.USER_NOTIFICATION
    ) {
      return null;
    }

    if (
      comment.notificationType === CommentNotificationType.MESSENGER ||
      !comment.emailStatus
    ) {
      return (
        <span
          className="seen"
          data-for="commentTooltip"
          data-tip={
            associatedBug?.session?.email
              ? `Sent as chat message with email fallback.`
              : `Sent as chat message.`
          }
        >
          <i className="fa-light fa-message-lines" />
          {comment.sessionNotificationsUnread ? 'Not seen yet' : 'Seen'} ·
        </span>
      );
    }

    const emailOpened = comment.emailStatusHistory?.find((status) => {
      return status.event === 'Open';
    })
      ? true
      : false;

    return (
      <span
        className="seen"
        data-for="commentTooltip"
        data-tip={`<div class="emailstatus-header">Email history:</div>${comment.emailStatusHistory
          ?.map((status, index) => {
            return `${historyTextFromStatus(status)}`;
          })
          .join('')}`}
      >
        <i className="fa-light fa-envelope" />
        {emailOpened ? 'Email opened' : textFromStatus(comment.emailStatus)} ·
      </span>
    );
  };

  const tooltipData = `<div class="user-tooltip"><div class="user-tooltip-title">${user.name
    }</div>${user.email && user.email.length > 5
      ? `<div class="user-tooltip-link"><a href="mailto:${user.email}">${user.email}</a></div>`
      : ''
    }</div>`;

  const renderSystemInfoComment = () => {
    return (
      <div className="comment comment--system">
        <div className="comment-body">
          <div className="body">
            <div className="comment-text">
              <NewlineText text={getCommentContent(comment)} />
            </div>
          </div>
        </div>
        <div className="comment-footer">
          {renderEmailStatus()}
          <TimeAgo formatter={formatter} date={comment.createdAt} />
        </div>
      </div>
    );
  };

  const renderIntegrationInfo = () => {
    return (
      <div className="comment comment--system">
        <div className="comment-body">
          <div className="body">
            <div className="comment-text">
              {getIntegrationCommentContent(comment)}
            </div>
          </div>
        </div>
        <div className="comment-footer">
          {renderEmailStatus()}
          <TimeAgo formatter={formatter} date={comment.createdAt} />
        </div>
      </div>
    );
  };

  const isFileImage = (file) => {
    return file && file['type'].split('/')[0] === 'image';
  };

  const renderAttachments = () => {
    if (!comment.attachments) {
      return <></>;
    }

    return (
      <div className="attachments comment-attachments mt-5">
        {comment.attachments.map((attachment, index) => {
          if (isFileImage(attachment)) {
            return (
              <img
                key={index}
                onClick={() => {
                  setShowAttachmentImageLightBox(true);
                  setPickedAttachmentUrl(attachment.url);
                }}
                className="attachment-image"
                src={attachment.url}
                alt={attachment.name}
              />
            );
          }

          return (
            <div
              className="attachment"
              key={index}
              onClick={() => {
                axios
                  .get(attachment.url, { responseType: 'blob' })
                  .then((response) => {
                    const url = window.URL.createObjectURL(
                      new Blob([response.data]),
                    );

                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', attachment.name);
                    document.body.appendChild(link);

                    link.click();
                    link.remove();
                  });
              }}
            >
              <a className="cursor-focus">
                <SaveIcon />
                {attachment.name}
              </a>
            </div>
          );
        })}
      </div>
    );
  };

  const renderFeedbackUpdateInfoComment = () => {
    const renderFeedbackUpdateContent = () => {
      if (comment.data.type === 'PRIORITY') {
        return (
          <>
            <UserAvatar isOnline={user.isOnline} email={user.email} small />{' '}
            <b>{comment.user.firstName}</b> changed the priority to{' '}
            <b>{BugPriorities[comment.data.value]?.title}.</b>
          </>
        );
      }

      if (comment.data.type === 'ARCHIVED') {
        return (
          <>
            <UserAvatar isOnline={user.isOnline} email={user.email} small />{' '}
            <b>{comment.user.firstName}</b> archived this feedback item.
          </>
        );
      }

      if (comment.data.type === 'RESTORED') {
        return (
          <>
            <UserAvatar isOnline={user.isOnline} email={user.email} small />{' '}
            <b>{comment.user.firstName}</b> restored this feedback item.
          </>
        );
      }

      if (comment.data.type === 'STATUS') {
        let status = comment.data.value;

        for (let i = 0; i < possibleStatusList.length; i++) {
          const statusItem = possibleStatusList[i];
          if (statusItem.key === status) {
            status = statusItem.title;
          }
        }

        return (
          <>
            <UserAvatar isOnline={user.isOnline} email={user.email} small />{' '}
            <b>{comment.user.firstName}</b> changed the status to{' '}
            <b>{status}.</b>
          </>
        );
      }

      if (comment.data.type === 'PROCESSING_USER') {
        const getAssignee = () => {
          if (comment.data.value.id === comment.user.id) {
            return <>themself.</>;
          }

          return (
            <>
              <UserAvatar
                isOnline={user.isOnline}
                email={comment.data.value.email}
                small
              />{' '}
              <b>{comment.data.value.firstName}.</b>
            </>
          );
        };

        return (
          <>
            <UserAvatar isOnline={user.isOnline} email={user.email} small />{' '}
            <b>{comment.user.firstName}</b> assigned this ticket to{' '}
            {getAssignee()}
          </>
        );
      }

      if (comment.data.type === 'TYPE') {
        let type = comment.data.value;

        for (let i = 0; i < feedbackTypes.length; i++) {
          const feedbackType = feedbackTypes[i];
          if (feedbackType.type === type) {
            type = feedbackType.name;
          }
        }

        return (
          <>
            <UserAvatar isOnline={user.isOnline} email={user.email} small />{' '}
            <b>{comment.user.firstName}</b> changed the feedback type to{' '}
            <b>{type}.</b>
          </>
        );
      }

      return <></>;
    };

    return (
      <div className="comment comment--system">
        <div className="comment-body">
          <div className="comment-statusupdate">
            {renderFeedbackUpdateContent()}
          </div>
        </div>
        <div className="comment-footer">
          {renderEmailStatus()}
          <TimeAgo formatter={formatter} date={comment.createdAt} />
        </div>
      </div>
    );
  };

  if (!shared && comment.type === CommentTypes.USER_NOTIFICATION) {
    return renderSystemInfoComment();
  }

  if (comment.type === CommentTypes.FEEDBACK_UPDATED) {
    return renderFeedbackUpdateInfoComment();
  }

  if (comment.type === CommentTypes.INTEGRATION_STATUS_UPDATED) {
    return renderIntegrationInfo();
  }

  const renderMainContent = () => {
    if (comment.data?.showReplyTimes) {
      return "Sent opening times to user.";
    }

    var contentData = comment.data?.content;
    if (!contentData) {
      return null;
    }

    if (
      contentData &&
      (typeof contentData === 'string' || contentData instanceof String)
    ) {
      contentData = contentData.replaceAll('\n', '<br>');
    }

    try {
      if (
        typeof contentData === 'object' &&
        !Array.isArray(contentData) &&
        contentData &&
        associatedBug &&
        associatedBug?.session
      ) {
        contentData = JSON.parse(
          contentWithVariables(JSON.stringify(contentData), {
            name: cleanupName(getSessionName(associatedBug?.session), 50, true),
          }),
        );
      }
    } catch (exp) { }

    return <RichTextEditor editable={false} content={contentData} />;
  };

  return (
    <div
      className={`comment ${hideCreator && 'comment--creator-hidden'} ${comment.id
        } ${(comment.type === CommentTypes.BOT_REPLY ||
          comment.type === CommentTypes.BOT_INPUT_REPLY ||
          comment.type === CommentTypes.USER_TEXT ||
          comment.type === CommentTypes.SHARED_COMMENT) &&
        'comment--external'
        } ${comment.type === CommentTypes.NOTE && 'comment--is-note'}`}
    >
      <div className="comment-body">
        <div data-for="commentTooltip" data-tip={tooltipData}>
          <div
            className={`shared-avatar ${shared &&
              comment.type === CommentTypes.TEXT &&
              `shared-avatar--team`
              }`}
          >
            <UserAvatar
              isOnline={user.isOnline}
              email={user.email}
              imageUrl={user.profileImageUrl}
              small
            />
          </div>
        </div>
        <div className="body">
          <div className="comment-text">
            {comment.type === CommentTypes.BOT_INPUT_REPLY && (<div className='comment-text-question'>{comment.data?.question}</div>)}
            {comment.data && comment.data.text && (
              <NewlineText text={getCommentContent(comment)} />
            )}
            {comment.data && renderMainContent()}
            {comment.attachments &&
              comment.attachments.length > 0 &&
              renderAttachments()}
          </div>
          {!hideFooter && (
            <div className="comment-time">
              {comment.sending ? (
                <div className="seen">
                  {comment.type === CommentTypes.NOTE
                    ? 'Saving...'
                    : 'Sending...'}
                </div>
              ) : (
                <>
                  {renderEmailStatus()}
                  <TimeAgo formatter={formatter} date={comment.createdAt} />
                </>
              )}
              {shared && comment.type === CommentTypes.TEXT && (
                <span className="shared-team-member">· Team member</span>
              )}
            </div>
          )}
        </div>
        {!shared && onDeleteComment && (
          <div
            className="trash-icon-comment-container"
            onClick={() => {
              Swal.fire({
                text: 'Do you really want to delete this comment?',
                showCancelButton: true,
                confirmButtonText: `Yes`,
                denyButtonText: `No`,
              }).then(async (result) => {
                if (result.isConfirmed) {
                  onDeleteComment();
                }
              });
            }}
          >
            <i className="fa-solid fa-trash" />
          </div>
        )}
      </div>
      {showAttachmentImageLightBox && pickedAttachmentUrl !== '' && (
        <Lightbox
          mainSrc={pickedAttachmentUrl}
          onCloseRequest={() => setShowAttachmentImageLightBox(false)}
        />
      )}
    </div>
  );
};

export default Comment;

export const CommentSkeleton = () => {
  return (
    <div className="comment comment--external">
      <div className="comment-body">
        <div className="shared-avatar">
          <PublicSkeleton
            height="34px"
            width="34px"
            style={{
              borderRadius: '34px',
            }}
          />
        </div>
        <div className="body">
          <div className="comment-text">
            <PublicSkeleton height="16px" width={`200px`} />
            <PublicSkeleton height="16px" width={`180px`} />
          </div>
          <div className="comment-footer">
            <PublicSkeleton height="8px" width={`30px`} />
          </div>
        </div>
      </div>
    </div>
  );
};
